import React, { useState, useEffect } from 'react';
import { useUser, useClerk } from '@clerk/clerk-react';
import { 
  LayoutDashboard, FileText, Settings, LogOut, User, Home,
  CheckCircle, Download, ShieldCheck, Plus, Trash2, ChevronDown, ChevronUp, Search
} from 'lucide-react';

// ── Your Clerk user ID — only this user sees the Admin tab ──────────────────
const ADMIN_USER_ID = 'user_3Er3sVny7DENDEdddTYSdmZevLn';
const ADMIN_KEY     = 'sc-admin-2026-spiralkey';
// ────────────────────────────────────────────────────────────────────────────

const MOCK_INVOICES = [
  { id: 'INV-2025-041', date: '01 Jun 2025', amount: '£1,200.00', status: 'paid',    description: 'Monthly retainer — June 2025' },
  { id: 'INV-2025-032', date: '01 May 2025', amount: '£1,200.00', status: 'paid',    description: 'Monthly retainer — May 2025' },
  { id: 'INV-2025-024', date: '01 Apr 2025', amount: '£1,450.00', status: 'paid',    description: 'Monthly retainer + project work' },
  { id: 'INV-2025-018', date: '01 Mar 2025', amount: '£1,200.00', status: 'paid',    description: 'Monthly retainer — March 2025' },
  { id: 'INV-2025-062', date: '15 Jun 2025', amount: '£340.00',   status: 'pending', description: 'Additional creative assets' },
];

const invoiceStatusConfig = {
  paid:    { label: 'Paid',    color: 'text-green-400', bg: 'bg-green-400/10 border-green-400/20' },
  pending: { label: 'Pending', color: 'text-amber-400', bg: 'bg-amber-400/10 border-amber-400/20' },
  overdue: { label: 'Overdue', color: 'text-red-400',   bg: 'bg-red-400/10  border-red-400/20'   },
};

export const ClientDashboard = ({ theme, isDarkMode, onClose, onSignOutSuccess }) => {
  const { user } = useUser();
  const { signOut } = useClerk();
  const [activeTab, setActiveTab] = useState('services');

  // Package state — loaded from the database via API
  const [clientPackage, setClientPackage] = useState(null);
  const [packageLoading, setPackageLoading] = useState(true);

  const fetchClientPackage = () => {
    if (!user?.id) return;
    setPackageLoading(true);
    fetch('/api/client-package.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clerk_user_id: user.id })
    })
      .then(r => r.json())
      .then(data => {
        if (data.success && data.package) setClientPackage(data.package);
        else setClientPackage(null);
      })
      .catch(() => setClientPackage(null))
      .finally(() => setPackageLoading(false));
  };

  // Fetch package on mount and whenever the services tab is opened
  useEffect(() => {
    if (activeTab === 'services') fetchClientPackage();
  }, [user?.id, activeTab]);

  const firstName = user?.firstName || user?.primaryEmailAddress?.emailAddress?.split('@')[0] || '';
  const isAdmin = user?.id === ADMIN_USER_ID;

  // Admin state
  const [adminClients, setAdminClients]       = useState([]);
  const [adminLoading, setAdminLoading]       = useState(false);
  const [adminExpanded, setAdminExpanded]     = useState(null); // clerk_user_id of expanded row
  const [adminForm, setAdminForm]             = useState(null); // null or package object being edited
  const [adminSaving, setAdminSaving]         = useState(false);
  const [adminMsg, setAdminMsg]               = useState(null);
  const [adminSearch, setAdminSearch]         = useState('');
  const [showNewForm, setShowNewForm]         = useState(false);

  const EMPTY_PKG = {
    clerk_user_id: '', company_name: '', package_name: 'Web Presence',
    package_tier: 'Essential', since_date: '', renewal_date: '',
    core_services: [{ id: 'domain', label: '', detail: '' }],
    addons: [{ id: 'addon1', label: '', detail: '', paid: false }],
  };

  const fetchAdminClients = async () => {
    setAdminLoading(true);
    try {
      const res = await fetch('/api/admin-package-list.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': ADMIN_KEY },
        body: JSON.stringify({ action: 'list' })
      });
      const data = await res.json();
      if (data.success) setAdminClients(data.packages);
    } catch(e) { console.error(e); }
    finally { setAdminLoading(false); }
  };

  useEffect(() => {
    if (activeTab === 'admin' && isAdmin) fetchAdminClients();
  }, [activeTab, isAdmin]);

  const savePackage = async (pkg) => {
    setAdminSaving(true);
    setAdminMsg(null);
    try {
      const res = await fetch('/api/admin-package.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': ADMIN_KEY },
        body: JSON.stringify(pkg)
      });
      const data = await res.json();
      if (data.success) {
        setAdminMsg({ text: 'Saved successfully', ok: true });
        setAdminForm(null);
        setShowNewForm(false);
        setAdminExpanded(null);
        fetchAdminClients();
        // If we just saved our own package, refresh the services tab too
        if (pkg.clerk_user_id === user?.id) fetchClientPackage();
      } else {
        setAdminMsg({ text: data.error || 'Error saving', ok: false });
      }
    } catch(e) {
      setAdminMsg({ text: 'Network error', ok: false });
    }
    setAdminSaving(false);
  };

  const deletePackage = async (clerk_user_id, company_name) => {
    if (!window.confirm(`Delete package for ${company_name || clerk_user_id}?`)) return;
    try {
      const res = await fetch('/api/admin-package-list.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': ADMIN_KEY },
        body: JSON.stringify({ action: 'delete', clerk_user_id })
      });
      const data = await res.json();
      if (data.success) fetchAdminClients();
    } catch(e) { console.error(e); }
  };

  const tabs = [
    { id: 'services', label: 'Services',  icon: LayoutDashboard },
    { id: 'invoices', label: 'Invoices',  icon: FileText },
    { id: 'settings', label: 'Account',   icon: User },
    ...(isAdmin ? [{ id: 'admin', label: 'Admin', icon: ShieldCheck }] : []),
  ];

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">

      {/* Header */}
      <div className="mb-10">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <img src="/sc_logo_notext.svg" alt="Spiral Creations" className="h-7 w-auto flex-shrink-0" />
              <p className={`text-xs font-sans tracking-[0.3em] uppercase ${theme.text.muted}`}>Client Portal</p>
            </div>
            <h1 className={`font-sans font-black uppercase ${theme.text.primary} text-2xl sm:text-4xl`} style={{ letterSpacing: '0.05em' }}>
              {(() => {
                const h = new Date().getHours();
                const greeting = h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
                return firstName && firstName !== 'there' ? `${greeting}, ${firstName}` : greeting;
              })()}
            </h1>
            {user?.lastSignInAt && (
              <p className={`${theme.text.muted} text-xs font-sans mt-1`}>
                Last login {new Date(user.lastSignInAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} at {new Date(user.lastSignInAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3" style={{marginTop: '10px'}}>
            <button
              onClick={() => { onClose(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className={`flex items-center gap-1.5 text-xs font-sans ${theme.text.muted} hover:text-pink-400 transition-colors`}
            >
              <Home size={13} />
              Home
            </button>
            <button
              onClick={() => { onClose(); signOut().then(() => { if (window.__scSignOutToast) window.__scSignOutToast(); }); }}
              className={`flex items-center gap-1.5 text-xs font-sans ${theme.text.muted} hover:text-red-400 transition-colors`}
            >
              <LogOut size={13} style={{transform: 'scaleX(-1)'}} />
              Sign out
            </button>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px mt-6" style={{ background: 'linear-gradient(to right, #eb008b, transparent)' }} />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-8">
        {tabs.map(tab => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-sans font-medium transition-all duration-200 ${
                active
                  ? 'text-white'
                  : `${theme.text.muted} hover:${theme.text.secondary}`
              }`}
              style={active ? { background: '#eb008b' } : {}}
            >
              <Icon size={15} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Services Tab */}
      {activeTab === 'services' && (
        <div className="w-full">
          <div className="max-w-2xl">

          {/* Loading state */}
          {packageLoading && (
            <div className={`${theme.card} border rounded-2xl p-12 flex items-center justify-center`}>
              <div className="flex flex-col items-center gap-3">
                <svg className="animate-spin w-6 h-6 text-pink-400" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                </svg>
                <p className={`text-xs font-sans ${theme.text.muted}`}>Loading your package…</p>
              </div>
            </div>
          )}

          {/* No package assigned yet */}
          {!packageLoading && !clientPackage && (
            <div className={`${theme.card} border rounded-2xl overflow-hidden`}>
              <style>{`
                @keyframes floatIcon {
                  0%, 100% { transform: translateY(0px); }
                  50% { transform: translateY(-6px); }
                }
                @keyframes fadeUpIn {
                  from { opacity: 0; transform: translateY(12px); }
                  to   { opacity: 1; transform: translateY(0); }
                }
                .no-pkg-icon  { animation: floatIcon 3s ease-in-out infinite; }
                .no-pkg-row-1 { animation: fadeUpIn 0.4s ease both; }
                .no-pkg-row-2 { animation: fadeUpIn 0.4s ease 0.1s both; }
                .no-pkg-row-3 { animation: fadeUpIn 0.4s ease 0.2s both; }
                .no-pkg-row-4 { animation: fadeUpIn 0.4s ease 0.3s both; }
              `}</style>

              {/* Top accent bar */}
              <div style={{ height: 3, background: 'linear-gradient(to right, #eb008b, transparent)' }} />

              <div className="px-8 py-10 flex flex-col items-center text-center gap-6">

                {/* Avatar */}
                <div className="no-pkg-icon w-16 h-16 rounded-2xl overflow-hidden flex-shrink-0" style={{ border: '2px solid rgba(235,0,139,0.3)', boxShadow: '0 0 20px rgba(235,0,139,0.15)' }}>
                  {user?.imageUrl
                    ? <img src={user.imageUrl} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center" style={{ background: 'rgba(235,0,139,0.08)' }}>
                        <span className="font-sans font-bold text-xl" style={{ color: '#eb008b' }}>
                          {user?.firstName?.[0] ?? '?'}
                        </span>
                      </div>
                  }
                </div>

                {/* Heading */}
                <div className="no-pkg-row-1">
                  <p className={`font-sans font-black uppercase text-lg ${theme.text.primary}`} style={{ letterSpacing: '0.05em' }}>No package assigned yet</p>
                  <p className={`text-xs font-sans ${theme.text.muted} mt-1`}>Your account is set up — we just need to configure your services.</p>
                </div>

                {/* What will appear here */}
                <div className={`no-pkg-row-2 w-full rounded-xl p-5 text-left space-y-3`} style={{ background: isDarkMode ? 'rgba(15,23,42,0.4)' : 'rgba(248,250,252,0.8)', border: isDarkMode ? '1px solid rgba(71,85,105,0.3)' : '1px solid rgba(229,231,235,0.8)' }}>
                  <p className={`text-[10px] font-sans tracking-[0.2em] uppercase ${theme.text.muted} mb-3`}>What you'll see here</p>
                  {[
                    ['Your active services', 'Domain, hosting, and any other core services'],
                    ['Included add-ons', 'Maintenance, security updates, and more'],
                    ['Renewal information', 'Package dates and review schedule'],
                  ].map(([title, desc], i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-1 h-1 rounded-full mt-2 flex-shrink-0" style={{ background: '#eb008b' }} />
                      <div>
                        <p className={`text-xs font-sans font-medium ${theme.text.secondary}`}>{title}</p>
                        <p className={`text-[11px] font-sans ${theme.text.muted}`}>{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <div className="no-pkg-row-3 flex flex-col items-center gap-2">
                  <button
                    onClick={() => { if (typeof onClose === 'function') onClose(); setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 500); }}
                    className="text-sm font-sans font-medium px-6 py-2.5 rounded-lg text-white transition-opacity hover:opacity-90"
                    style={{ background: '#eb008b' }}
                  >
                    Get in touch
                  </button>
                  <p className={`text-[11px] font-sans ${theme.text.muted}`}>We'll have you set up in no time.</p>
                </div>

              </div>
            </div>
          )}

          {/* Package card */}
          {!packageLoading && clientPackage && (
          <div className={`${theme.card} border rounded-2xl overflow-hidden`}>

            {/* Card header — package identity */}
            <div className="px-8 pt-8 pb-6" style={{ borderBottom: '1px solid rgba(235,0,139,0.2)' }}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className={`text-[11px] font-sans tracking-[0.25em] uppercase mb-1 ${theme.text.muted}`}>
                    Active Package
                  </p>
                  <h2 className={`font-sans font-black text-2xl uppercase ${theme.text.primary}`} style={{ letterSpacing: '0.04em' }}>
                    {clientPackage.name}
                  </h2>
                  <p className="text-xs font-sans mt-1" style={{ color: '#eb008b' }}>
                    {clientPackage.tier} tier &nbsp;·&nbsp; since {clientPackage.since}
                  </p>
                </div>
                <div className="flex-shrink-0 flex items-center gap-1.5 text-xs font-sans font-medium text-green-400 bg-green-400/10 border border-green-400/20 px-3 py-1.5 rounded-full mt-1">
                  <span style={{ position: 'relative', width: 10, height: 10, flexShrink: 0, display: 'inline-block' }}>
                    <span style={{ position: 'absolute', top: 1, left: 1, width: 8, height: 8, borderRadius: '50%', backgroundColor: '#4ade80', animation: 'pulseRing 2s ease-out infinite', opacity: 0 }} />
                    <span style={{ position: 'absolute', top: 1, left: 1, width: 8, height: 8, borderRadius: '50%', backgroundColor: '#4ade80', animation: 'pulseRing 2s ease-out infinite', animationDelay: '1s', opacity: 0 }} />
                    <span style={{ position: 'absolute', top: 1, left: 1, width: 8, height: 8, borderRadius: '50%', backgroundColor: '#4ade80' }} />
                  </span>
                  All systems active
                </div>
              </div>
            </div>

            {/* Core services */}
            {clientPackage.coreServices?.length > 0 && (
            <div className="px-8 pt-6 pb-4">
              <p className={`text-[10px] font-sans tracking-[0.2em] uppercase mb-4 ${theme.text.muted}`}>Core Services</p>
              <div className="space-y-3">
                {clientPackage.coreServices.map((svc, i) => (
                  <div key={svc.id || i} className="flex items-start gap-4">
                    <div className="flex flex-col items-center pt-1" style={{ minWidth: 2 }}>
                      <div className="w-0.5 rounded-full flex-1" style={{ background: '#eb008b', minHeight: 32, opacity: 0.7 }} />
                    </div>
                    <div className="flex-1 min-w-0 pb-3" style={i < clientPackage.coreServices.length - 1 ? { borderBottom: isDarkMode ? '1px solid rgba(71,85,105,0.3)' : '1px solid rgba(229,231,235,0.8)' } : {}}>
                      <p className={`font-sans font-semibold text-sm ${theme.text.primary}`}>{svc.label}</p>
                      <p className={`font-sans text-xs mt-0.5 ${theme.text.muted}`}>{svc.detail}</p>
                    </div>
                    <CheckCircle size={14} className="text-green-400 flex-shrink-0 mt-1" />
                  </div>
                ))}
              </div>
            </div>
            )}

            {/* Add-ons */}
            {clientPackage.addons?.length > 0 && (
            <div className="px-8 pt-4 pb-6" style={{ borderTop: isDarkMode ? '1px solid rgba(71,85,105,0.3)' : '1px solid rgba(229,231,235,0.8)' }}>
              <p className={`text-[10px] font-sans tracking-[0.2em] uppercase mb-4 ${theme.text.muted}`}>Included Add-ons</p>
              <div className="space-y-0">
                {clientPackage.addons.map((addon, i) => (
                  <div
                    key={addon.id || i}
                    className="flex items-start gap-3 py-3"
                    style={i < clientPackage.addons.length - 1 ? { borderBottom: isDarkMode ? '1px solid rgba(71,85,105,0.2)' : '1px solid rgba(229,231,235,0.6)' } : {}}
                  >
                    <CheckCircle size={13} className="text-green-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className={`font-sans text-sm font-medium ${theme.text.secondary}`}>{addon.label}</p>
                      <p className={`font-sans text-xs mt-0.5 ${theme.text.muted}`}>{addon.detail}</p>
                    </div>
                    {!addon.paid && (
                      <span className="flex-shrink-0 text-[10px] font-sans tracking-wide text-green-400/80 border border-green-400/20 rounded px-1.5 py-0.5 mt-0.5">
                        FREE
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
            )}

            {/* Card footer — renewal */}
            <div
              className="px-8 py-4 flex items-center justify-between"
              style={{ borderTop: isDarkMode ? '1px solid rgba(71,85,105,0.3)' : '1px solid rgba(229,231,235,0.8)', background: isDarkMode ? 'rgba(15,23,42,0.3)' : 'rgba(248,250,252,0.6)' }}
            >
              <p className={`text-xs font-sans ${theme.text.muted}`}>
                Renews <span className={theme.text.secondary}>{clientPackage.renewalDate}</span>
              </p>
              <button
                onClick={() => { if (typeof onClose === 'function') onClose(); setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 500); }}
                className="text-xs font-sans transition-colors hover:text-pink-400"
                style={{ color: '#eb008b' }}
              >
                Discuss your package →
              </button>
            </div>

          </div>
          )}

          {!packageLoading && clientPackage && (
            <p className={`text-xs font-sans ${theme.text.muted} text-center mt-5`}>
              <button onClick={() => { if (typeof onClose === 'function') onClose(); setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 500); }} className="text-pink-400 hover:text-pink-300 transition-colors">Contact us</button> to make changes to your package.
            </p>
          )}
        </div>
        </div>
      )}

      {/* Invoices Tab */}
      {activeTab === 'invoices' && (
        <div>
          <div className={`${theme.card} border rounded-2xl overflow-hidden`}>
            <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-gray-200'}`}>
              <h3 className={`font-sans font-semibold text-sm ${theme.text.primary}`}>Billing History</h3>
            </div>
            <div className="divide-y" style={{ borderColor: isDarkMode ? 'rgba(71,85,105,0.3)' : 'rgba(229,231,235,0.8)' }}>
              {MOCK_INVOICES.map(inv => {
                const status = invoiceStatusConfig[inv.status];
                return (
                  <div key={inv.id} className="flex items-center justify-between px-6 py-4 hover:bg-black/5 transition-colors">
                    <div className="flex items-center gap-4">
                      <div>
                        <p className={`text-sm font-sans font-medium ${theme.text.primary}`}>{inv.id}</p>
                        <p className={`text-xs font-sans ${theme.text.muted}`}>{inv.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`text-xs font-sans ${theme.text.muted} hidden sm:block`}>{inv.date}</span>
                      <span className={`font-sans font-semibold text-sm ${theme.text.primary}`}>{inv.amount}</span>
                      <span className={`text-xs font-sans px-2.5 py-1 rounded-full border ${status.color} ${status.bg}`}>
                        {status.label}
                      </span>
                      <button className={`${theme.text.muted} hover:text-pink-400 transition-colors`}>
                        <Download size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <p className={`text-xs font-sans ${theme.text.muted} text-center mt-6`}>
            Invoice data shown is for illustration. Contact us for your actual billing history.
          </p>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="max-w-lg">
          <div className={`${theme.card} border rounded-2xl overflow-hidden`}>
            <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-gray-200'}`}>
              <h3 className={`font-sans font-semibold text-sm ${theme.text.primary}`}>Account Details</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className={`text-xs font-sans ${theme.text.muted} block mb-1`}>Full Name</label>
                <div className={`${theme.input} rounded-lg px-4 py-3 text-sm font-sans`}>
                  {user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : '—'}
                </div>
              </div>
              <div>
                <label className={`text-xs font-sans ${theme.text.muted} block mb-1`}>Email Address</label>
                <div className={`${theme.input} rounded-lg px-4 py-3 text-sm font-sans`}>
                  {user?.primaryEmailAddress?.emailAddress}
                </div>
              </div>
              <div>
                <label className={`text-xs font-sans ${theme.text.muted} block mb-1`}>Member Since</label>
                <div className={`${theme.input} rounded-lg px-4 py-3 text-sm font-sans`}>
                  {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
                </div>
              </div>
              <div>
                <label className={`text-xs font-sans ${theme.text.muted} block mb-1`}>Last Login</label>
                <div className={`${theme.input} rounded-lg px-4 py-3 text-sm font-sans`}>
                  {user?.lastSignInAt ? `${new Date(user.lastSignInAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} at ${new Date(user.lastSignInAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : '—'}
                </div>
              </div>
            </div>
            <p className={`text-xs font-sans ${theme.text.muted} px-6 pb-4 text-center`}>
              Need to update your details? <button onClick={() => { onClose(); setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 500); }} className="text-pink-400 hover:text-pink-300 transition-colors">Contact us</button>
            </p>
          </div>


        </div>
      )}

      {/* Admin Tab */}
      {activeTab === 'admin' && isAdmin && (
        <div>

          {/* Page header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className={`font-sans font-bold text-base ${theme.text.primary}`}>Client Packages</h2>
              <p className={`text-xs font-sans ${theme.text.muted} mt-0.5`}>{adminClients.length} client{adminClients.length !== 1 ? 's' : ''} configured</p>
            </div>
            <button
              onClick={() => { setAdminForm({ ...EMPTY_PKG }); setShowNewForm(true); setAdminExpanded(null); }}
              className="flex items-center gap-1.5 text-xs font-sans font-medium text-white px-3 py-2 rounded-lg transition-opacity hover:opacity-90"
              style={{ background: '#eb008b' }}
            >
              <Plus size={13} /> New client
            </button>
          </div>

          {/* Success / error message */}
          {adminMsg && (
            <div className={`text-xs font-sans px-4 py-3 rounded-lg mb-4 ${adminMsg.ok ? 'text-green-400 bg-green-400/10 border border-green-400/20' : 'text-red-400 bg-red-400/10 border border-red-400/20'}`}>
              {adminMsg.text}
            </div>
          )}

          {/* New client form */}
          {showNewForm && adminForm && (
            <AdminPackageForm
              pkg={adminForm}
              onChange={setAdminForm}
              onSave={() => savePackage(adminForm)}
              onCancel={() => { setShowNewForm(false); setAdminForm(null); setAdminMsg(null); }}
              saving={adminSaving}
              isDarkMode={isDarkMode}
              theme={theme}
              isNew
            />
          )}

          {/* Search */}
          {adminClients.length > 0 && (
            <div className="relative mb-4">
              <Search size={13} className={`absolute left-3 top-1/2 -translate-y-1/2 ${theme.text.muted}`} />
              <input
                type="text"
                placeholder="Search clients…"
                value={adminSearch}
                onChange={e => setAdminSearch(e.target.value)}
                className={`w-full ${theme.input} rounded-lg pl-8 pr-4 py-2.5 text-xs font-sans`}
              />
            </div>
          )}

          {/* Client list */}
          {adminLoading ? (
            <div className="flex justify-center py-12">
              <svg className="animate-spin w-5 h-5 text-pink-400" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
              </svg>
            </div>
          ) : (
            <div className="space-y-2">
              {adminClients
                .filter(c => !adminSearch || c.company_name.toLowerCase().includes(adminSearch.toLowerCase()) || c.clerk_user_id.toLowerCase().includes(adminSearch.toLowerCase()))
                .map(client => {
                  const expanded = adminExpanded === client.clerk_user_id;
                  const editing  = expanded && adminForm?.clerk_user_id === client.clerk_user_id;
                  return (
                    <div key={client.clerk_user_id} className={`${theme.card} border rounded-xl overflow-hidden transition-all`}>
                      {/* Row header */}
                      <div className="flex items-center justify-between px-5 py-4 cursor-pointer" onClick={() => {
                        if (expanded) { setAdminExpanded(null); setAdminForm(null); }
                        else { setAdminExpanded(client.clerk_user_id); setAdminForm({ ...client }); setShowNewForm(false); }
                      }}>
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: '#4ade80' }} />
                          <div className="min-w-0">
                            <p className={`font-sans font-semibold text-sm ${theme.text.primary} truncate`}>{client.company_name || '—'}</p>
                            <p className={`text-[11px] font-sans ${theme.text.muted} truncate`}>{client.package_name} · {client.package_tier} · renews {client.renewal_date || '—'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                          <button
                            onClick={e => { e.stopPropagation(); deletePackage(client.clerk_user_id, client.company_name); }}
                            className={`${theme.text.muted} hover:text-red-400 transition-colors`}
                          >
                            <Trash2 size={13} />
                          </button>
                          {expanded ? <ChevronUp size={14} className={theme.text.muted} /> : <ChevronDown size={14} className={theme.text.muted} />}
                        </div>
                      </div>

                      {/* Expanded edit form */}
                      {expanded && adminForm && (
                        <div className={`border-t px-5 py-5 ${isDarkMode ? 'border-slate-700/50' : 'border-gray-200'}`}>
                          <AdminPackageForm
                            pkg={adminForm}
                            onChange={setAdminForm}
                            onSave={() => savePackage(adminForm)}
                            onCancel={() => { setAdminExpanded(null); setAdminForm(null); setAdminMsg(null); }}
                            saving={adminSaving}
                            isDarkMode={isDarkMode}
                            theme={theme}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              {adminClients.length === 0 && !showNewForm && (
                <p className={`text-xs font-sans ${theme.text.muted} text-center py-10`}>No clients yet. Click <strong>New client</strong> to add one.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Reusable package form ────────────────────────────────────────────────────
const AdminPackageForm = ({ pkg, onChange, onSave, onCancel, saving, isDarkMode, theme, isNew }) => {
  const set = (field, val) => onChange(prev => ({ ...prev, [field]: val }));

  const setCore = (i, field, val) => {
    const arr = [...pkg.core_services];
    arr[i] = { ...arr[i], [field]: val };
    onChange(prev => ({ ...prev, core_services: arr }));
  };
  const addCore = () => onChange(prev => ({ ...prev, core_services: [...prev.core_services, { id: 'svc' + Date.now(), label: '', detail: '' }] }));
  const removeCore = i => onChange(prev => ({ ...prev, core_services: prev.core_services.filter((_, idx) => idx !== i) }));

  const setAddon = (i, field, val) => {
    const arr = [...pkg.addons];
    arr[i] = { ...arr[i], [field]: val };
    onChange(prev => ({ ...prev, addons: arr }));
  };
  const addAddon = () => onChange(prev => ({ ...prev, addons: [...prev.addons, { id: 'addon' + Date.now(), label: '', detail: '', paid: false }] }));
  const removeAddon = i => onChange(prev => ({ ...prev, addons: prev.addons.filter((_, idx) => idx !== i) }));

  const inputCls = `w-full ${theme.input} rounded-lg px-3 py-2 text-xs font-sans`;
  const labelCls = `text-[10px] font-sans tracking-[0.15em] uppercase ${theme.text.muted} block mb-1`;
  const sectionCls = `text-[10px] font-sans tracking-[0.2em] uppercase ${theme.text.muted} mb-3 mt-5`;

  return (
    <div className="space-y-3">
      {isNew && (
        <div>
          <label className={labelCls}>Clerk user ID</label>
          <input className={inputCls} placeholder="user_2abc123..." value={pkg.clerk_user_id} onChange={e => set('clerk_user_id', e.target.value)} />
        </div>
      )}
      {!isNew && (
        <p className={`text-[10px] font-sans ${theme.text.muted} font-mono`}>{pkg.clerk_user_id}</p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Company name</label>
          <input className={inputCls} placeholder="Acme Ltd" value={pkg.company_name} onChange={e => set('company_name', e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Package name</label>
          <input className={inputCls} placeholder="Web Presence" value={pkg.package_name} onChange={e => set('package_name', e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Tier</label>
          <input className={inputCls} placeholder="Essential" value={pkg.package_tier} onChange={e => set('package_tier', e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Since date</label>
          <input className={inputCls} placeholder="June 2025" value={pkg.since_date} onChange={e => set('since_date', e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Renewal date</label>
          <input className={inputCls} placeholder="1 June 2026" value={pkg.renewal_date} onChange={e => set('renewal_date', e.target.value)} />
        </div>
      </div>

      {/* Core services */}
      <p className={sectionCls}>Core services</p>
      {pkg.core_services.map((svc, i) => (
        <div key={i} className="grid grid-cols-[1fr_1.4fr_24px] gap-2 items-center">
          <input className={inputCls} placeholder="Label" value={svc.label} onChange={e => setCore(i, 'label', e.target.value)} />
          <input className={inputCls} placeholder="Detail" value={svc.detail} onChange={e => setCore(i, 'detail', e.target.value)} />
          <button onClick={() => removeCore(i)} className={`${theme.text.muted} hover:text-red-400 transition-colors text-lg leading-none`}>×</button>
        </div>
      ))}
      <button onClick={addCore} className={`text-[11px] font-sans ${theme.text.muted} hover:text-pink-400 transition-colors`}>+ Add service</button>

      {/* Add-ons */}
      <p className={sectionCls}>Add-ons</p>
      {pkg.addons.map((addon, i) => (
        <div key={i} className="grid grid-cols-[1fr_1.4fr_56px_24px] gap-2 items-center">
          <input className={inputCls} placeholder="Label" value={addon.label} onChange={e => setAddon(i, 'label', e.target.value)} />
          <input className={inputCls} placeholder="Detail" value={addon.detail} onChange={e => setAddon(i, 'detail', e.target.value)} />
          <label className={`flex items-center gap-1.5 text-[11px] font-sans ${theme.text.muted} cursor-pointer`}>
            <input type="checkbox" checked={addon.paid} onChange={e => setAddon(i, 'paid', e.target.checked)} />
            Paid
          </label>
          <button onClick={() => removeAddon(i)} className={`${theme.text.muted} hover:text-red-400 transition-colors text-lg leading-none`}>×</button>
        </div>
      ))}
      <button onClick={addAddon} className={`text-[11px] font-sans ${theme.text.muted} hover:text-pink-400 transition-colors`}>+ Add add-on</button>

      {/* Actions */}
      <div className="flex items-center gap-3 pt-3">
        <button
          onClick={onSave}
          disabled={saving}
          className="text-xs font-sans font-medium text-white px-4 py-2 rounded-lg transition-opacity hover:opacity-90 disabled:opacity-50"
          style={{ background: '#eb008b' }}
        >
          {saving ? 'Saving…' : 'Save package'}
        </button>
        <button onClick={onCancel} className={`text-xs font-sans ${theme.text.muted} hover:${theme.text.secondary} transition-colors`}>
          Cancel
        </button>
      </div>
    </div>
  );
};